/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import { useLocale } from '@/hooks/useLocale';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { getCountryFromLocale } from '@/utils/getCountryFromLocale';
import { useProductReviews } from '../api/getProductReviews';
import type { ProductReview, ProductReviewImage, ProductReviewsData } from '../interface';

export type ExtendedProductReviewImage = ProductReviewImage & {
  reviewID: string;
};

export type ProductReviewImageResult = {
  images: ExtendedProductReviewImage[] | null;
  reviews: ProductReview[] | null;
};

export function useProductReviewsImages(): ProductReviewImageResult {
  const locale = useLocale();
  const { data: productDetails } = useProductDetails();
  const { data } = useProductReviews({
    country: getCountryFromLocale(locale),
    customerType: 'B2C',
    skuId: productDetails?.product?.itemId ?? '',
    reviewsWithMedia: true,
    enabled: !!productDetails?.product?.itemId,
    select: selectImages,
  });

  return data ?? { images: null, reviews: null };
}

const selectImages = ({ reviews }: ProductReviewsData): ProductReviewImageResult => {
  return {
    reviews,
    images: reviews.flatMap((review) =>
      review.images.map((image) => ({ ...image, reviewID: review.id }))
    ),
  };
};
